import React, { useEffect, useState, useCallback } from "react";
import { useFetch } from "api";
import { endpoints } from "config";
import { ShoppingCartItem } from "components/ShoppingCart";
import { ShopItemResponse } from "./ShopItemView.types";

// Global reference to close any open shop item view
let globalCloseShopItemView: (() => void) | null = null;

/**
 * Hook to close any open shop item view from external components
 */
export const useCloseShopItemView = () => {
  const closeShopItemView = useCallback(() => {
    if (globalCloseShopItemView) {
      globalCloseShopItemView();
    }
  }, []);

  return { closeShopItemView };
};

/**
 * Custom hook for managing the shop item view state
 */
export const useShopItemView = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShoppingCartItem | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);

  // Fetch shop item details when selectedItemId changes
  const shouldFetch = selectedItemId !== null;

  // Only call useFetch when we have a selectedItemId
  const fetchResult = useFetch<ShopItemResponse>(
    shouldFetch ? endpoints.shop.view : "",
    shouldFetch ? { params: { Id: selectedItemId } } : {}
  );

  // Override the result when we shouldn't be fetching
  const { data: shopItemData, loading, error } = shouldFetch
    ? fetchResult
    : { data: undefined, loading: false, error: undefined };

  const open = (item: ShoppingCartItem) => {
    setSelectedItem(item);
    setSelectedItemId(item.id);
    setQuantity(1);
    // Small delay to ensure the panel is rendered before animation starts
    setTimeout(() => {
      setIsOpen(true);
    }, 10);
  };

  const close = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSelectedItem(null);
      setSelectedItemId(null);
    }, 300); // Wait for animation to complete
  };

  // Register this instance's close function globally
  useEffect(() => {
    globalCloseShopItemView = close;

    // Cleanup on unmount
    return () => {
      if (globalCloseShopItemView === close) {
        globalCloseShopItemView = null;
      }
    };
  }, [close]);

  const toggle = (item?: ShoppingCartItem) => {
    if (isOpen) {
      close();
    } else if (item) {
      open(item);
    }
  };

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    setQuantity(prev => Math.max(1, prev - 1));
  };

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return {
    // State
    isOpen,
    selectedItem,
    selectedItemId,
    quantity,
    shopItemData,
    loading,
    error,
    // Actions
    open,
    close,
    toggle,
    incrementQuantity,
    decrementQuantity,
  };
};
